import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './auth.css';
import { AuthShell, Field, PasswordField } from './AuthShell';
import { authService } from '../api/auth';
import type { RegisterRole } from '../api/auth';
import { CoinIcon, SuvIcon, TagIcon } from '../../../commodity/components/caria';

/**
 * Register — the "Crear cuenta" tab of
 * docs/html-prototypes/CAR-IA/car-ia-login.html, with the fields this app
 * actually submits: nombre, apellido, correo, contraseña + confirmación, and
 * the role selector (BUYER / SELLER_PRIVATE / SELLER_DEALER). The role
 * selector has no prototype equivalent — the prototype has a single "Nombre
 * completo" field — so it is styled in the prototype's card language.
 * Behaviour is unchanged: register → /verify-email carrying the email.
 */

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  accepted?: string;
}

const ROLES: {
  value: RegisterRole;
  label: string;
  Icon: React.FC<{ size?: number; strokeWidth?: number }>;
}[] = [
  { value: 'BUYER', label: 'Comprador', Icon: SuvIcon },
  { value: 'SELLER_PRIVATE', label: 'Particular', Icon: TagIcon },
  { value: 'SELLER_DEALER', label: 'Dealer', Icon: CoinIcon },
];

const Register: React.FC = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<RegisterRole>('BUYER');
  // The backend RegisterDto models Terms and Privacy as two booleans, but a
  // user cannot meaningfully accept one and refuse the other, so a single
  // control naming both documents sets both. Split it if Legal wants them
  // recorded independently.
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const clear = (k: keyof FormErrors) => {
    setErrors((p) => ({ ...p, [k]: undefined }));
    setApiError('');
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!firstName.trim()) next.firstName = 'El nombre es obligatorio.';
    if (!lastName.trim()) next.lastName = 'El apellido es obligatorio.';
    if (!email.trim()) {
      next.email = 'El correo es obligatorio.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Escribe un correo válido.';
    }
    if (!password) {
      next.password = 'La contraseña es obligatoria.';
    } else if (password.length < 8) {
      next.password = 'La contraseña debe tener al menos 8 caracteres.';
    }
    if (!confirmPassword) {
      next.confirmPassword = 'Confirma tu contraseña.';
    } else if (password !== confirmPassword) {
      next.confirmPassword = 'Las contraseñas no coinciden.';
    }
    if (!accepted) {
      next.accepted = 'Debes aceptar los Términos y la Política de Privacidad.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError('');
    try {
      await authService.register({
        email,
        password,
        firstName,
        lastName,
        role,
        acceptedTerms: accepted,
        acceptedPrivacy: accepted,
      });
      navigate('/verify-email', { state: { email } });
    } catch (err: any) {
      setApiError(err.message || 'No pudimos crear la cuenta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell tab="register" title="Crea tu cuenta" subtitle="Gratis, toma menos de un minuto.">
      {apiError && (
        <div className="cia-auth-alert cia-auth-alert--error" role="alert">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <span className="cia-auth-roles-label">Soy…</span>
        <div className="cia-auth-roles" role="radiogroup" aria-label="Tipo de cuenta">
          {ROLES.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={role === value}
              className={`cia-auth-role${role === value ? ' on' : ''}`}
              onClick={() => setRole(value)}
            >
              <Icon size={20} strokeWidth={1.7} />
              {label}
            </button>
          ))}
        </div>

        <div className="cia-auth-row">
          <Field
            id="firstName"
            label="Nombre"
            value={firstName}
            onChange={(v) => {
              setFirstName(v);
              clear('firstName');
            }}
            error={errors.firstName}
            autoComplete="given-name"
          />
          <Field
            id="lastName"
            label="Apellido"
            value={lastName}
            onChange={(v) => {
              setLastName(v);
              clear('lastName');
            }}
            error={errors.lastName}
            autoComplete="family-name"
          />
        </div>

        <Field
          id="email"
          label="Correo"
          type="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            clear('email');
          }}
          error={errors.email}
          autoComplete="email"
        />
        <PasswordField
          id="password"
          label="Contraseña"
          value={password}
          onChange={(v) => {
            setPassword(v);
            clear('password');
          }}
          error={errors.password}
          autoComplete="new-password"
        />
        <PasswordField
          id="confirmPassword"
          label="Repite la contraseña"
          value={confirmPassword}
          onChange={(v) => {
            setConfirmPassword(v);
            clear('confirmPassword');
          }}
          error={errors.confirmPassword}
          autoComplete="new-password"
        />

        <div className="cia-auth-consent">
          <label>
            <input
              type="checkbox"
              checked={accepted}
              aria-invalid={errors.accepted ? 'true' : undefined}
              onChange={(e) => {
                setAccepted(e.target.checked);
                clear('accepted');
              }}
            />
            <span>
              Acepto los{' '}
              <button type="button" onClick={() => navigate('/legal')}>
                Términos
              </button>{' '}
              y la{' '}
              <button type="button" onClick={() => navigate('/legal')}>
                Política de Privacidad
              </button>
              .
            </span>
          </label>
          {errors.accepted && <span className="cia-auth-fld-err">{errors.accepted}</span>}
        </div>

        <button type="submit" className="cia-auth-go" style={{ marginTop: 8 }} disabled={loading}>
          {loading ? <span className="cia-auth-spin" aria-label="Cargando" /> : 'Crear cuenta'}
        </button>

        <p className="cia-auth-alt">
          ¿Ya tienes cuenta?{' '}
          <button type="button" onClick={() => navigate('/login')}>
            Ingresar
          </button>
        </p>
      </form>
    </AuthShell>
  );
};

export default Register;
